import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError, fetchCard, type CardResult } from '../api';

type Status = 'loading' | 'success' | 'invalid' | 'error';

export default function CardPage() {
  const { signedToken = '' } = useParams<{ signedToken: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<CardResult | null>(null);

  useEffect(() => {
    if (!signedToken) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    fetchCard(signedToken)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 400 || err.status === 404)) {
          setStatus('invalid');
          return;
        }
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [signedToken]);

  if (status === 'loading') {
    return (
      <main className="container" aria-busy="true">
        <div className="spinner" role="status">
          <span className="sr-only">Loading your card…</span>
        </div>
      </main>
    );
  }

  if (status === 'invalid') {
    return (
      <main className="container">
        <h1>Card not found</h1>
        <p>Try tapping the link in your email or text message to access your card.</p>
      </main>
    );
  }

  if (status === 'error') {
    return (
      <main className="container">
        <h1>Something went wrong</h1>
        <p>We couldn&apos;t load your card. Please try again.</p>
      </main>
    );
  }

  const d = data!;
  return (
    <main className="container card-page">
      {d.venue && <h1>{d.venue.name}</h1>}
      {d.offer && (
        <>
          <p className="offer-title">{d.offer.title}</p>
          <p className="offer-body">{d.offer.body}</p>
        </>
      )}

      <div className="wallet-cta">
        <a href={d.card_download_url} className="add-to-wallet" aria-label="Add to Apple Wallet">
          <span className="add-to-wallet__apple" aria-hidden="true"></span>
          Add to Apple Wallet
        </a>
      </div>

      <p className="instructions">{d.instructions}</p>

      {d.offer?.terms && (
        <p className="fine-print">
          {d.offer.terms} &mdash; <a href="/legal/consent">Terms &amp; privacy</a>
        </p>
      )}
    </main>
  );
}
